import {
  Build as BuildIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
  MoreVert as MoreVertIcon,
  Print as PrintIcon,
  Sync as SyncIcon,
} from "@mui/icons-material";
import { SwipeableDrawer } from "@mui/material";
import { styled } from "@mui/material/styles";
import { unwrapResult } from "@reduxjs/toolkit";
import { EditMachineLocationDialog } from "components/job/machine/EditMachineLocationDialog";
import PrimaryButton from "components/PrimaryButton";
import StyledFab from "components/StyledFab";
import { isAbortError } from "helpers";
import technicianSettings from "helpers/technicianSettings";
import { TabProps } from "models/TabProps";
import { UpdateMachineInputType } from "operations/schema/schema";
import { CreateJobDialog } from "pages/jobs/dialogs/CreateJobDialog";
import { FC, useCallback, useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useAppDispatch, useAppSelector } from "store";
import { selectConnectionStatus, selectPageSelectedJob } from "store/root.store";
import { setOpen } from "store/slices/dialog.store";
import {
  selectCreateJobOpen,
  setOpen as setOpenCreateJobDialog,
} from "store/slices/dialogs/createJob.store";
import {
  selectIsJobInProgress,
  setChangeJobMachineOpen,
  setRejectJobOpen,
  updateMachineLocation,
  updateVisit,
} from "store/slices/jobs.store";
import { isFlagEnabled } from "store/slices/user.store";
import { RejectJobDialog } from "./RejectJobDialog";

const StyledContainer = styled("div")(() => ({
  zIndex: "9001",
}));

export const JobDetailsActions: FC<TabProps> = (props) => {
  const { setFab } = props;
  const dispatch = useAppDispatch();
  const isOnline = useAppSelector(selectConnectionStatus);
  const job = useAppSelector(selectPageSelectedJob);
  const { editMachineLoading } = useAppSelector((s) => s.jobs);
  const openCreateJobDialog = useAppSelector(selectCreateJobOpen);
  const canCreateJob = technicianSettings.canCreateJob && isOnline;
  const changeJobMachineEnabled = useAppSelector((s) => isFlagEnabled(s, "ChangeJobMachine"));
  const canChangeMachine =
    technicianSettings.isAllowedMobileMachineEdit && isOnline && changeJobMachineEnabled;
  const [openDrawer, setOpenDrawer] = useState(false);
  const jobInProgress = useAppSelector(selectIsJobInProgress);

  useEffect(() => {
    const fab = (
      <StyledFab onClick={() => setOpenDrawer(true)} data-testid="jobDetailsActionsOpenButton">
        <MoreVertIcon />
      </StyledFab>
    );
    if (setFab) {
      setFab(fab);
    }
  }, [setFab]);

  const updateMachineLocationCb = useCallback(
    async (machine: UpdateMachineInputType) => {
      let ret = { success: false, queued: false, abort: false };
      await dispatch(updateMachineLocation({ jobId: job.id, machine }))
        .then(unwrapResult)
        .then(({ queued }) => {
          ret.success = true;
          ret.queued = !!queued;
        })
        .catch((e) => {
          ret.abort = isAbortError(e);
        });
      return ret;
    },
    [dispatch, job.id],
  );

  return (
    <StyledContainer data-testid="jobDetailsActionsContainer">
      <SwipeableDrawer
        anchor="bottom"
        open={openDrawer}
        onOpen={() => setOpenDrawer(true)}
        onClose={() => setOpenDrawer(false)}
      >
        <div style={{ margin: "0px 16px" }} data-testid="jobDetailsActionsDrawer">
          {!jobInProgress && (
            <PrimaryButton
              data-testid="rejectJobButton"
              onClick={() => dispatch(setRejectJobOpen({ open: true }))}
              startIcon={<CancelIcon />}
              style={{ textTransform: "none" }}
            >
              <FormattedMessage id="general.rejectJob" />
            </PrimaryButton>
          )}
          <PrimaryButton
            data-testid="syncOrderLines"
            onClick={() => dispatch(updateVisit({ jobId: job.id }))}
            startIcon={<SyncIcon />}
            style={{ textTransform: "none" }}
          >
            <FormattedMessage id="general.syncOrderLines" />
          </PrimaryButton>
          {canCreateJob && (
            <PrimaryButton
              data-testid="createJobButton"
              onClick={() => dispatch(setOpenCreateJobDialog({ open: true }))}
              startIcon={<BuildIcon />}
              style={{ textTransform: "none" }}
            >
              <FormattedMessage id="menu.createNewJob" />
            </PrimaryButton>
          )}
          {canChangeMachine && (
            <PrimaryButton
              data-testid="changeJobMachineButton"
              onClick={() => dispatch(setChangeJobMachineOpen({ open: true }))}
              startIcon={<EditIcon />}
              style={{ textTransform: "none" }}
            >
              <FormattedMessage id="visit.changeMachine" />
            </PrimaryButton>
          )}
          {job.machine?.id && (
            <PrimaryButton
              data-testid="machineEditButton"
              onClick={() => dispatch(setOpen({ dialogName: "editMachineLocation", open: true }))}
              startIcon={<PrintIcon />}
              style={{ textTransform: "none" }}
            >
              <FormattedMessage id="visit.editMachineLocation" />
            </PrimaryButton>
          )}
        </div>
      </SwipeableDrawer>
      {canCreateJob && openCreateJobDialog && <CreateJobDialog customer={job.customer} />}
      {job.machine && (
        <EditMachineLocationDialog
          machine={job.machine}
          externalId={job.externalId}
          loading={editMachineLoading}
          updateMachine={updateMachineLocationCb}
        />
      )}
      <RejectJobDialog />
    </StyledContainer>
  );
};
